/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 *
 * Design System — DataTable pagination footer.
 *
 * Reports intent through its callbacks; it never slices data. Exported on its own
 * so tables that render their own frame can reuse it.
 *
 * Two modes, because the product's APIs come in two shapes. Page mode knows the
 * total row count and can therefore work out the last page. Cursor mode is told
 * only whether more rows exist — several list APIs return `{ hasMore,
 * hasPrevious }` with no count at all, so a total cannot be required.
 */
import cx from 'classnames';
import type { ReactNode } from 'react';

import type { DataTablePaginationProps } from '../../types';
import { IconButton } from '../IconButton';
import { Select } from '../Select';

/** Matches the options the product's existing tables offer. */
const DEFAULT_PAGE_SIZE_OPTIONS = [15, 25, 50, 100, 200];

/**
 * `1-25 of 3,544` when a total is known, `1-25` when it is not, `0 of 0` when
 * there is nothing to page through.
 */
const defaultRange = ({ from, to, total }: { from: number; to: number; total?: number }): ReactNode => {
  if (total === 0) return '0 of 0';

  // No rows on this page: show the total alone rather than an empty range.
  if (from < 1 || to < from) return total === undefined ? '' : `0 of ${total.toLocaleString()}`;

  const range = `${from.toLocaleString()}-${to.toLocaleString()}`;

  return total === undefined ? range : `${range} of ${total.toLocaleString()}`;
};

const DataTablePagination = (props: DataTablePaginationProps) => {
  const { pageSize, pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS, onPageSizeChange, labels, className } = props;

  /*
   * Cursor APIs sometimes report a page number and sometimes do not. Without one
   * the range cannot be numbered, so only a total (when present) is shown.
   */
  const page = props.mode === 'cursor' ? props.page : props.page;
  const total = props.mode === 'cursor' ? props.totalCount : props.total;

  const isFirstPage = props.mode === 'cursor' ? !props.hasPrevious : props.page <= 1;
  const isLastPage =
    props.mode === 'cursor' ? !props.hasMore : props.page >= Math.max(1, Math.ceil(props.total / props.pageSize));

  const handlePrevious = () => {
    if (props.mode === 'cursor') {
      props.onPreviousPage();
      return;
    }
    props.onPageChange(props.page - 1);
  };

  const handleNext = () => {
    if (props.mode === 'cursor') {
      props.onNextPage();
      return;
    }
    props.onPageChange(props.page + 1);
  };

  const options = pageSizeOptions.map((size) => ({ value: String(size), label: String(size) }));
  const renderRange = labels?.range ?? defaultRange;

  const rangeLabel = (() => {
    // A cursor API that reports neither a page nor a total has nothing to say.
    if (page === undefined) {
      return total === undefined ? null : renderRange({ from: 0, to: 0, total });
    }

    const from = (page - 1) * pageSize + 1;
    const to = total === undefined ? page * pageSize : Math.min(page * pageSize, total);

    return renderRange({ from, to, total });
  })();

  return (
    <div className={cx('ds-data-table__footer', className)}>
      {onPageSizeChange && (
        <div className="ds-data-table__page-size">
          <span className="ds-data-table__footer-label">{labels?.pageSize ?? 'Items per Page'}</span>
          <Select
            size="small"
            value={String(pageSize)}
            options={options}
            onChange={(value) => onPageSizeChange(Number(value))}
            aria-label="Items per page"
            className="ds-data-table__page-size-select"
          />
        </div>
      )}

      <div className="ds-data-table__pager">
        {rangeLabel !== null && <span className="ds-data-table__footer-label">{rangeLabel}</span>}
        <div className="ds-data-table__pager-buttons">
          <IconButton
            variant="secondary"
            size="medium"
            icon="actionable/chevron-left"
            aria-label={labels?.previousPage ?? 'Previous page'}
            disabled={isFirstPage}
            onClick={handlePrevious}
          />
          <IconButton
            variant="secondary"
            size="medium"
            icon="actionable/chevron-right"
            aria-label={labels?.nextPage ?? 'Next page'}
            disabled={isLastPage}
            onClick={handleNext}
          />
        </div>
      </div>
    </div>
  );
};

DataTablePagination.displayName = 'DataTablePagination';

export default DataTablePagination;
